import { Box, Paper, Stack, Typography } from '@mui/material';

import { organizationType } from 'constant';
import useTranslate from 'hooks/useTranslate';
import { OrganizationInterface } from 'types';

type DetailKey = keyof OrganizationInterface | 'has_applied_successfully' | 'applied_at';

interface DetailField {
    key: DetailKey;
    label: string;
}

const IDENTIFICATION_FIELDS: DetailField[] = [
    { key: 'company_name', label: 'Business Name' },
    { key: 'legal_entity_name', label: 'Legal name of the business' },
    { key: 'vat_number', label: 'Tax number' },
    { key: 'address', label: 'Business address' },
    { key: 'city', label: 'city' },
    { key: 'zip_code', label: 'postalCode' },
    { key: 'region', label: 'Province where the business operates' },
    { key: 'organization_type', label: 'What type of organization are you?' },
    { key: 'date_established', label: 'Year the business was established' },
];

const ADDITIONAL_FIELDS: DetailField[] = [
    { key: 'industry', label: 'Industry of the business' },
    {
        key: 'products_description',
        label: 'Brief description of products or services offered by the business',
    },
    {
        key: 'operates_from_physical_location',
        label: 'Does the business operate from a physical commercial location?',
    },
    {
        key: 'number_employees',
        label: 'Total number of employees currently working in the business',
    },
    {
        key: 'number_canadian_employees',
        label: 'Number of employees who are Canadian citizens or permanent residents',
    },
    { key: 'annual_revenue', label: 'Approximate annual revenue of the business' },
    {
        key: 'has_been_banned_from_hiring_in_canada',
        label: 'Has the business ever been banned or restricted from hiring or sponsoring foreign workers in Canada?',
    },
    {
        key: 'applied_for_lmia_prior',
        label: 'Has your company ever applied for an LMIA in the past?',
    },
];

const LMIA_FIELDS: DetailField[] = [
    { key: 'has_applied_successfully', label: 'Was it successful?' },
    { key: 'applied_at', label: 'When did you apply?' },
];

const organizationTypeLabel = Object.entries(organizationType).reduce<Record<string, string>>(
    (labels, [label, value]) => {
        labels[String(value)] = label;
        return labels;
    },
    {}
);

const hasValue = (value: unknown) => value !== null && value !== undefined && value !== '';

interface OrganizationProfileDetailsProps {
    profile: OrganizationInterface | null;
}

const OrganizationProfileDetails = ({ profile }: OrganizationProfileDetailsProps) => {
    const { translate } = useTranslate();
    const record = (profile ?? {}) as OrganizationInterface & Record<string, unknown>;
    const showLmiaDetails = record.applied_for_lmia_prior === true;

    const formatValue = (key: DetailKey) => {
        const value = record[key];
        if (!hasValue(value)) {
            return { text: '-', missing: true };
        }
        if (typeof value === 'boolean') {
            return { text: translate(value ? 'yes' : 'no'), missing: false };
        }
        if (key === 'organization_type') {
            const label = organizationTypeLabel[String(value)] ?? String(value);
            return { text: translate(label), missing: false };
        }
        if (
            key === 'industry' ||
            key === 'region' ||
            key === 'annual_revenue' ||
            key === 'has_been_banned_from_hiring_in_canada'
        ) {
            return { text: translate(String(value)), missing: false };
        }
        return { text: String(value), missing: false };
    };

    const renderSection = (title: string, fields: DetailField[]) => (
        <Paper variant="elevation" sx={{ p: 2 }}>
            <Typography
                variant="subtitle1"
                sx={{ pb: 1, mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
            >
                {translate(title)}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    columnGap: 3,
                    rowGap: 1,
                }}
            >
                {fields.map((field) => {
                    const { text, missing } = formatValue(field.key);

                    return (
                        <Box key={field.key} sx={{ minWidth: 0 }}>
                            <Typography variant="body2" color="text.secondary">
                                {translate(field.label)}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    mt: 0.25,
                                    fontWeight: missing ? 400 : 500,
                                    color: missing ? 'text.secondary' : 'text.primary',
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {text}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );

    return (
        <Stack spacing={2}>
            {renderSection('organizationIdentification', IDENTIFICATION_FIELDS)}
            {renderSection(
                'additionalDetails',
                showLmiaDetails ? [...ADDITIONAL_FIELDS, ...LMIA_FIELDS] : ADDITIONAL_FIELDS
            )}
        </Stack>
    );
};

export default OrganizationProfileDetails;
